interface MaskOptions {
  // 动态截取模式（支持负数索引）
  maskStart?: number // 脱敏起始位置（包含，从0开始），负数表示从末尾开始
  maskEnd?: number // 脱敏结束位置（不包含），负数表示从末尾开始
  // 传统模式（与动态模式互斥）
  startLen?: number
  endLen?: number
  // 掩码配置
  maskChar?: string // 掩码字符，默认 '*'
  maskCount?: number // 掩码字符数量
  // 全展示阈值
  keepFullLength?: number
  // 长度不足时的行为
  insufficientBehavior?: 'mask' | 'show' | 'hide'
  // 掩码插入方式
  insertMode?: 'replace' | 'append' | 'prepend' // 替换原字符/追加到原位置前/追加到原位置后
}

function maskString(str: string, options: MaskOptions = {}): string {
  const {
    // 动态截取模式
    maskStart,
    maskEnd,
    // 传统模式
    startLen = 3,
    endLen = 3,
    // 掩码配置
    maskChar = '*',
    maskCount = 3,
    // 全展示阈值
    keepFullLength = 6,
    // 长度不足时的行为
    insufficientBehavior = 'mask',
    // 掩码插入方式
    insertMode = 'replace',
  } = options

  if (typeof str !== 'string') return ''
  const totalLength = str.length

  // 1. 处理全展示阈值
  if (totalLength <= keepFullLength) {
    return str
  }

  // 2. 生成掩码字符串
  const mask = maskChar.repeat(maskCount)

  // 3. 计算脱敏区间
  let replaceStart = 0
  let replaceEnd = 0

  if (maskStart !== undefined && maskEnd !== undefined) {
    // 处理负数索引
    const normalizeIndex = (index: number, length: number): number => {
      if (index < 0) {
        return Math.max(0, length + index) // 负数转换
      }
      return Math.min(index, length) // 确保不超过长度
    }

    replaceStart = normalizeIndex(maskStart, totalLength)
    replaceEnd = normalizeIndex(maskEnd, totalLength)

    // 确保 start <= end
    if (replaceStart > replaceEnd) {
      ;[replaceStart, replaceEnd] = [replaceEnd, replaceStart]
    }
  } else {
    // 使用传统模式
    if (totalLength <= startLen + endLen) {
      // 长度不足以脱敏
      switch (insufficientBehavior) {
        case 'show':
          return str
        case 'hide':
          return ''
        case 'mask':
        default:
          return mask
      }
    }
    replaceStart = startLen
    replaceEnd = totalLength - endLen
  }

  // 4. 特殊处理：如果脱敏区间无效或为0
  if (replaceStart >= replaceEnd) {
    // 脱敏区间无效，根据配置处理
    switch (insufficientBehavior) {
      case 'show':
        return str
      case 'hide':
        return ''
      case 'mask':
      default:
        return mask
    }
  }

  // 5. 根据插入模式构建结果
  switch (insertMode) {
    case 'replace':
      // 替换脱敏区间
      return str.slice(0, replaceStart) + mask + str.slice(replaceEnd)
    case 'append':
      // 在脱敏区间后追加掩码
      return str.slice(0, replaceEnd) + mask + str.slice(replaceEnd)
    case 'prepend':
      // 在脱敏区间前追加掩码
      return str.slice(0, replaceStart) + mask + str.slice(replaceStart)
    default:
      return str.slice(0, replaceStart) + mask + str.slice(replaceEnd)
  }
}

// // 示例使用：
// console.log('=== 支持负数索引 ===')
// console.log('显示最后3位，前面全部隐藏:')
// console.log(maskString('13800138000', { maskStart: 0, maskEnd: -3 })) // ***8000
// console.log(maskString('13800138000', { maskStart: 0, maskEnd: -4 })) // ***38000
// console.log(maskString('13800138000', { maskStart: 0, maskEnd: -3, maskCount: 4 })) // ****8000

// console.log('\n=== 各种负数组合 ===')
// // 显示最后4位，隐藏前面
// console.log(maskString('1234567890', { maskStart: 0, maskEnd: -4 })) // ***7890
// // 隐藏中间部分，显示前3后3
// console.log(maskString('1234567890', { maskStart: 3, maskEnd: -3 })) // 123***890
// // 从倒数第5位开始隐藏到倒数第2位
// console.log(maskString('1234567890', { maskStart: -5, maskEnd: -2 })) // 12345***90
// // 只隐藏最后2位
// console.log(maskString('1234567890', { maskStart: -2, maskEnd: 10 })) // 12345678**

// console.log('\n=== 复杂场景 ===')
// // 邮箱：隐藏@前的中间部分
// console.log('邮箱脱敏:')
// const email = 'example@email.com'
// const atIndex = email.indexOf('@')
// console.log(maskString(email, { maskStart: 3, maskEnd: atIndex })) // exa***@email.com
// console.log(maskString(email, { maskStart: 2, maskEnd: atIndex, maskCount: 4 })) // ex****@email.com

// // 身份证：显示前6后4
// console.log('\n身份证脱敏:')
// const idCard = '110101199001011234'
// console.log(maskString(idCard, { maskStart: 6, maskEnd: -4, maskCount: 8 })) // 110101********1234

// // 手机号：显示前3后4
// console.log('\n手机号脱敏:')
// const phone = '13800138000'
// console.log(maskString(phone, { maskStart: 3, maskEnd: -4, maskCount: 4 })) // 138****8000
// // 或者用负数
// console.log(maskString(phone, { maskStart: 3, maskEnd: -4 })) // 138***8000
// // 只显示最后4位
// console.log(maskString(phone, { maskStart: 0, maskEnd: -4 })) // ***38000

export { maskString }
